import { useApolloClient, useQuery } from '@apollo/client';
import { WorkflowObjectTypes } from '@generated/graphql';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogTitle,
} from '@ovComponents/2-component/dialog/dialog';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { Button, useToast } from 'hephaestus/web';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import {
  FETCH_DOWNLOAD_ALL_FORMS_CLIENT_GROUP,
  FETCH_DOWNLOAD_ALL_FORMS_USER,
  FETCH_FORM_COMPLETION_PDF,
} from './downloadAllForms.query';
import {
  collectFormDownloads,
  downloadSignedFile,
  type FormPdfDownloadResult,
  formPdfUrls,
  httpUrl,
  loadPdfBytes,
} from './formPdfDownload';
import {
  defaultFormsZipFileName,
  downloadZipFile,
  zipFormPdfs,
} from './formPdfZip';

export type DownloadAllFormsProp = {
  forms: { id: string; state?: string | null }[];
  objectId: string;
  objectType?: WorkflowObjectTypes | null;
};

export const DownloadAllForms = ({
  forms,
  objectId,
  objectType,
}: DownloadAllFormsProp) => {
  const { t } = useTranslation(['workflowCompletions', 'shared']);
  const { translateBackendString } = useLocalization();
  const client = useApolloClient();
  const { enqueueToast } = useToast();
  const [downloading, setDownloading] = useState(false);
  const [nameOpen, setNameOpen] = useState(false);
  const [fileName, setFileName] = useState('');
  const downloadableForms = forms.filter((form) => form.state !== 'ARCHIVED');
  const isHousehold = objectType === WorkflowObjectTypes.Household;
  const isUser = !objectType || objectType === WorkflowObjectTypes.User;
  const { data: userData, loading: userLoading } = useQuery(
    FETCH_DOWNLOAD_ALL_FORMS_USER,
    {
      variables: { userId: objectId! },
      skip: !isUser || !objectId,
    },
  );
  const { data: householdData, loading: householdLoading } = useQuery(
    FETCH_DOWNLOAD_ALL_FORMS_CLIENT_GROUP,
    {
      variables: { clientGroupId: objectId! },
      skip: !isHousehold || !objectId,
    },
  );
  const user = userData?.fetchUser?.user;
  const householdName = householdData?.fetchClientGroup?.clientGroup?.name;

  const loadFormPdf = async (
    formId: string,
  ): Promise<FormPdfDownloadResult> => {
    const { data } = await client.query({
      query: FETCH_FORM_COMPLETION_PDF,
      variables: { formCompletionId: formId },
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
    });
    const formCompletion = data?.fetchFormCompletion;
    if (!formCompletion) return { status: 'failed' };

    const urls = formPdfUrls(formCompletion);
    if (urls.length === 0) return { status: 'noFile' };

    const name =
      translateBackendString(formCompletion.resolvedPdfFileName) ||
      translateBackendString(formCompletion.translatedName) ||
      formCompletion.unsignedDocument?.fileName ||
      'form.pdf';
    const loaded = await loadPdfBytes(urls);
    if (loaded?.bytes) return { status: 'zip', name, bytes: loaded.bytes };
    if (loaded?.fallbackUrl) return { status: 'link', url: loaded.fallbackUrl };
    return { status: 'failed' };
  };

  const openNameDialog = () => {
    setFileName(
      defaultFormsZipFileName(
        isHousehold ? { householdName: householdName ?? '' } : (user ?? {}),
        new Date(),
      ),
    );
    setNameOpen(true);
  };

  const handleDownload = async (zipFileName: string) => {
    setDownloading(true);
    try {
      const loaded = await Promise.all(
        downloadableForms.map(async (form) => {
          try {
            return await loadFormPdf(form.id);
          } catch {
            return { status: 'failed' } satisfies FormPdfDownloadResult;
          }
        }),
      );
      const { zipEntries, linkUrls, attempted } = collectFormDownloads(loaded);
      const signedUrls = linkUrls.flatMap((url) => {
        const href = httpUrl(url);
        return href ? [href] : [];
      });
      const savedCount = zipEntries.length + signedUrls.length;

      if (savedCount === 0) {
        enqueueToast(t('workflowCompletions:downloadAllFormsError'), {
          variant: 'error',
        });
        return;
      }
      if (zipEntries.length > 0) {
        downloadZipFile(zipFormPdfs(zipEntries), zipFileName);
      }
      for (const url of signedUrls) {
        downloadSignedFile(url);
      }
      if (savedCount < attempted) {
        enqueueToast(t('workflowCompletions:downloadAllFormsPartialError'), {
          variant: 'error',
        });
      }
    } catch {
      enqueueToast(t('workflowCompletions:downloadAllFormsError'), {
        variant: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  const confirmDownload = () => {
    const zipFileName = fileName.trim();
    if (!zipFileName) return;
    setNameOpen(false);
    void handleDownload(zipFileName);
  };

  return (
    <>
      <Button
        type='button'
        onClick={openNameDialog}
        variant='contained'
        size='small'
        color='secondary'
        disabled={
          downloading ||
          downloadableForms.length === 0 ||
          (!!objectId && isUser && userLoading) ||
          (!!objectId && isHousehold && householdLoading)
        }
        loading={downloading}
        data-testid='download-all-forms-button'
      >
        {t('workflowCompletions:downloadAllForms')}
      </Button>
      <Dialog
        open={nameOpen}
        onClose={() => setNameOpen(false)}
        fullWidth
        maxWidth='sm'
      >
        <DialogTitle onClose={() => setNameOpen(false)}>
          {t('workflowCompletions:downloadAllForms')}
        </DialogTitle>
        <form
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            confirmDownload();
          }}
        >
          <DialogContent>
            <TextField
              label={t('workflowCompletions:downloadAllFormsFileName')}
              value={fileName}
              onChange={(event: { target: { value: string } }) =>
                setFileName(event.target.value)
              }
              fullWidth
              testId='download-all-forms-file-name'
            />
          </DialogContent>
          <DialogFooter>
            <Button
              type='button'
              variant='text'
              color='secondary'
              onClick={() => setNameOpen(false)}
            >
              {t('shared:cancel')}
            </Button>
            <Button
              type='submit'
              variant='contained'
              disabled={!fileName.trim() || downloading}
            >
              {t('shared:download')}
            </Button>
          </DialogFooter>
        </form>
      </Dialog>
    </>
  );
};
